import React, { lazy, Suspense } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { PublicSiteProvider } from "../../features/vacationPackagesPublic/PublicSiteContext";
import { Loader } from "../../Components/Loader/Loader";
import CustomLoading from "../../Components/GeneralComponents/CustomLoading/customloading";
import { ProductContextProvider } from "../../i18n/providers/ProductContextProvider";
import SeoMetaGuardRuntimeBridge from "../../seo/SeoMetaGuardRuntimeBridge";
import { TermsAndConditions } from "../../Pages/AgentTerms/AgentTerms";
import ContactPage from "../../Pages/Client/Contact/ContactPage";
import CustomerChatPublicStartPage from "../../Pages/Client/CustomerChatPublic/CustomerChatPublicStartPage";
import TripMasterOpsApp from "./TripMasterOpsApp";
import TripMasterUrlNormalizer from "./TripMasterUrlNormalizer";
import { toTripMasterHref } from "./tripMasterUrls";
import MetaPixelRouteTracker from "../../utils/metaPixel/MetaPixelRouteTracker";
import TripMasterStaticPageTitle from "./TripMasterStaticPageTitle";

const VacationPackagesPage = lazy(() =>
  import("../../Pages/Client/VacationPackages/VacationPackagesPage")
);
const LdsHypPaymentOutcomePage = lazy(() =>
  import("../../Pages/Client/Payment/LdsHypPaymentOutcomePage")
);
const SavedPackagesProfile = lazy(() =>
  import("../../Pages/Client/PackagesProfile/SavedPackageProfile")
);
const UnsavedPackagesProfile = lazy(() =>
  import("../../Pages/Client/PackagesProfile/UnsavedPackagesProfile")
);
const PackageOrderSuccessPage = lazy(() =>
  import("../../Pages/Client/PackagesProfile/PackageOrderSuccessPage")
);
const CustomerPortalShell = lazy(() => import("../../features/customerPortal/CustomerPortalShell"));
const PublicBookingConfirmationPage = lazy(() =>
  import("../../features/publicBookingConfirmation/PublicBookingConfirmationPage")
);
const PublicBookingAccessPage = lazy(() =>
  import("../../features/publicBookingConfirmation/PublicBookingAccessPage")
);
const PublicBookingViewPage = lazy(() =>
  import("../../features/publicBookingConfirmation/PublicBookingViewPage")
);
const PublicBookingSuccessPreviewPage = lazy(() =>
  import("../../features/publicBookingConfirmation/PublicBookingSuccessPreviewPage")
);

function TripMasterPublicPage() {
  return (
    <PublicSiteProvider>
      <Suspense fallback={<Loader />}>
        <VacationPackagesPage />
      </Suspense>
    </PublicSiteProvider>
  );
}

function TripMasterLegacyRedirect() {
  const location = useLocation();
  return <Navigate to={toTripMasterHref(location)} replace />;
}

function PaymentOutcome({ label }) {
  return (
    <Suspense fallback={<CustomLoading label={label} overlay size="large" />}>
      <LdsHypPaymentOutcomePage />
    </Suspense>
  );
}

/**
 * Dedicated TripMaster public site: short URLs, no HotelsRiver landing/chrome.
 */
export default function TripMasterAppRoutes() {
  return (
    <Router>
      <ProductContextProvider>
        <SeoMetaGuardRuntimeBridge />
        <TripMasterUrlNormalizer />
        <MetaPixelRouteTracker />
        <Routes>
          <Route path="/" element={<TripMasterPublicPage />} />
          <Route path="/search" element={<TripMasterPublicPage />} />
          <Route path="/packages" element={<TripMasterPublicPage />} />
          <Route path="/hotels" element={<TripMasterPublicPage />} />
          <Route path="/flights" element={<TripMasterPublicPage />} />
          <Route path="/package/:id/:hotelSlug/:checkIn/:checkOut" element={<TripMasterPublicPage />} />
          <Route path="/package/:id" element={<TripMasterPublicPage />} />
          <Route path="/destination/:destinationSlug" element={<TripMasterPublicPage />} />

          <Route path="/vacation-packages" element={<TripMasterLegacyRedirect />} />
          <Route path="/vacation-packages/*" element={<TripMasterLegacyRedirect />} />
          <Route path="/vacation_packages" element={<TripMasterLegacyRedirect />} />
          <Route path="/vacation_packages/*" element={<TripMasterLegacyRedirect />} />
          <Route path="/he/vacation-packages" element={<TripMasterLegacyRedirect />} />
          <Route path="/he/vacation-packages/*" element={<TripMasterLegacyRedirect />} />
          <Route path="/en/vacation-packages" element={<TripMasterLegacyRedirect />} />
          <Route path="/en/vacation-packages/*" element={<TripMasterLegacyRedirect />} />

          <Route path="/ops" element={<TripMasterOpsApp />} />
          <Route path="/ops/*" element={<TripMasterOpsApp />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/login/*" element={<Navigate to="/" replace />} />
          <Route path="/p/*" element={<Navigate to="/" replace />} />
          <Route path="/dashboard/*" element={<Navigate to="/" replace />} />
          <Route path="/welcome" element={<Navigate to="/" replace />} />
          <Route path="/terms" element={<Navigate to="/termsAndConditions" replace />} />
          <Route path="/termsAndConditions" element={<><TripMasterStaticPageTitle pageLabel="תנאי שימוש" /><TermsAndConditions /></>} />
          <Route path="/contact" element={<><TripMasterStaticPageTitle pageLabel="צור קשר" /><ContactPage /></>} />
          <Route path="/customer-chat/start" element={<><TripMasterStaticPageTitle pageLabel="צ׳אט שירות" /><CustomerChatPublicStartPage /></>} />
          <Route
            path="/customer-portal/*"
            element={
              <>
                <TripMasterStaticPageTitle pageLabel="פורטל לקוח" />
                <Suspense fallback={<CustomLoading label="טוען פורטל הזמנה..." overlay size="large" />}>
                  <CustomerPortalShell />
                </Suspense>
              </>
            }
          />
          <Route
            path="/booking/access"
            element={
              <PublicSiteProvider>
                <TripMasterStaticPageTitle pageLabel="כניסה להזמנה" />
                <Suspense fallback={<CustomLoading label="טוען כניסה להזמנה..." overlay size="large" />}>
                  <PublicBookingAccessPage />
                </Suspense>
              </PublicSiteProvider>
            }
          />
          <Route
            path="/booking/confirmation/:orderRef"
            element={
              <PublicSiteProvider>
                <TripMasterStaticPageTitle pageLabel="אישור הזמנה" />
                <Suspense fallback={<CustomLoading label="טוען אישור הזמנה..." overlay size="large" />}>
                  <PublicBookingConfirmationPage />
                </Suspense>
              </PublicSiteProvider>
            }
          />
          <Route
            path="/booking/view/:orderRef"
            element={
              <PublicSiteProvider>
                <TripMasterStaticPageTitle pageLabel="פרטי הזמנה" />
                <Suspense fallback={<CustomLoading label="טוען הזמנה..." overlay size="large" />}>
                  <PublicBookingViewPage />
                </Suspense>
              </PublicSiteProvider>
            }
          />
          <Route
            path="/packages/profile"
            element={
              <Suspense fallback={<CustomLoading label="טוען פרופיל חבילה..." overlay size="large" />}>
                <UnsavedPackagesProfile />
              </Suspense>
            }
          />
          <Route
            path="/packages/profile/:id/success"
            element={
              <Suspense fallback={<CustomLoading label="טוען אישור הזמנה..." overlay size="large" />}>
                <PackageOrderSuccessPage />
              </Suspense>
            }
          />
          <Route
            path="/packages/profile/:id"
            element={
              <Suspense fallback={<CustomLoading label="טוען פרופיל חבילה..." overlay size="large" />}>
                <SavedPackagesProfile />
              </Suspense>
            }
          />
          <Route path="/payment/success/lds/:orderId" element={<PaymentOutcome label="טוען אישור תשלום..." />} />
          <Route path="/payment/fail/lds/:orderId" element={<PaymentOutcome label="טוען..." />} />
          <Route path="/payment/cancel/lds/:orderId" element={<PaymentOutcome label="טוען..." />} />
          {import.meta.env.DEV ? (
            <Route
              path="/dev/public-booking-success-preview"
              element={
                <PublicSiteProvider>
                  <Suspense fallback={<CustomLoading label="טוען הדמיית אישור..." overlay size="large" />}>
                    <PublicBookingSuccessPreviewPage />
                  </Suspense>
                </PublicSiteProvider>
              }
            />
          ) : null}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ProductContextProvider>
    </Router>
  );
}
